import React from "react";
import { KeyIcon, LockClosedIcon, LockOpenIcon } from "@heroicons/react/20/solid";

/**
 * Renders a PHI status tag based on the combination of flags
 * Returns null if no tag should be shown
 */
export function renderPhiStatusTag(
    phiAnonymized?: boolean,
    phiDeanonymized?: boolean,
    phiDeanonymizationEnabled?: boolean,
    phiEncryptedMapAvailable?: boolean,
    phiViolation?: boolean
): React.ReactElement | null {
    // Red: Violation - deanonymized without permission
    if (phiViolation) {
        return (
            <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 border border-red-300"
                title="⚠️ SECURITY VIOLATION:\nAgent deanonymized PHI without server permission"
            >
                <LockOpenIcon className="h-3 w-3" />
                PHI VIOLATION
            </span>
        );
    }

    // Green: Protected and deanonymized with permission (successful deanonymization)
    if (phiAnonymized && phiDeanonymized && phiDeanonymizationEnabled) {
        return (
            <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 border border-green-300"
                title="PHI Protection Status:\n✓ Anonymized by server\n✓ Agent permitted to deanonymize\n✓ Deanonymized for display"
            >
                <KeyIcon className="h-3 w-3" />
                PHI Protected
            </span>
        );
    }

    // Orange: Protected with encrypted map (permission denied)
    if (phiAnonymized && !phiDeanonymized && phiEncryptedMapAvailable) {
        return (
            <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800 border border-orange-300"
                title="PHI Protection Status:\n✓ Anonymized by server\n⚠ Agent lacks direct deanonymization permission\n○ Encrypted map available"
            >
                <LockClosedIcon className="h-3 w-3" />
                PHI Encrypted
            </span>
        );
    }

    // Yellow: Protected (tokens displayed)
    if (phiAnonymized && !phiDeanonymized) {
        return (
            <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-300"
                title="PHI Protection Status:\n✓ Anonymized by server\n✗ Not deanonymized (tokens displayed)"
            >
                <LockClosedIcon className="h-3 w-3" />
                PHI Protected
            </span>
        );
    }

    // No tag for non-anonymized data
    return null;
}
